/**
 * Filtros de O.S. no mesmo shell de Tarefas, sem importar critérios de outro domínio.
 * RELEASE: 2026-10-16 v1.1.0 - Atalho "Abrir calendário de agendamentos" na barra de ações,
 *          igual ao da listagem de O.S. (mesmo ícone, rótulo e modal).
 */
import { useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { Kanban, Search, X, RefreshCw, List, Plus, SlidersHorizontal, CalendarDays } from 'lucide-react';
import { KanbanFilterLayout } from '@/components/kanban/KanbanFilterLayout';
import { DateFilterRow } from '@/components/kanban/KanbanDateFilterRow';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import { SERVICE_ORDER_DEADLINE_OPTIONS } from '@/lib/serviceOrderTiming';
import type { ServiceOrderKanbanFilters as Filters } from '@/hooks/useServiceOrders';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import { cn } from '@/lib/utils';

export function ServiceOrderKanbanFilters({ title, titleSlot, filters, onChange, search, onSearch, onRefresh, onList, onCreate, onCalendar }: {
  title: string; titleSlot?: ReactNode; filters: Filters; onChange: (f: Filters) => void;
  search: string; onSearch: (value: string) => void; onRefresh: () => void; onList?: () => void; onCreate?: () => void;
  /** v1.1.0: abre o modal de calendário de agendamentos de O.S. (BR-OS-031) */
  onCalendar?: () => void;
}) {
  const [expanded, setExpanded] = useState(!!search);
  const [more, setMore] = useState(false);
  const { serviceTypes } = useServiceTypes();
  const dateCount = Number(!!(filters.dateFrom || filters.dateTo));
  const count = Number(!!filters.serviceTypeId) + Number(!!filters.providerId) + Number(!!filters.deadlineBucket) + dateCount;
  const pill = cn(QUICK_FILTER_PILL, 'h-[22px] w-auto shrink-0 px-2 text-xs');
  
  const date = (value: string | null) => value ? new Date(`${value}T12:00:00`) : undefined;
  
  return (
    <KanbanFilterLayout
      title={titleSlot ?? <div className="flex items-center gap-2"><Kanban className="h-5 w-5 shrink-0 text-primary" /><h1 className="max-w-[160px] truncate text-lg font-semibold">{title}</h1></div>}
      search={
        <div className="flex shrink-0 items-center gap-1">
          {expanded && (
            <Input 
              autoFocus 
              aria-label="Buscar O.S." 
              placeholder="Buscar..." 
              maxLength={100} 
              value={search} 
              onChange={e => onSearch(e.target.value.replace(/[<>]/g, ''))} 
              onBlur={() => !search && setExpanded(false)} 
              className="h-7 w-32 rounded-full text-xs sm:w-44" 
            />
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 rounded-full" 
            title={expanded ? 'Fechar busca' : 'Buscar O.S.'} 
            aria-label={expanded ? 'Fechar busca' : 'Buscar O.S.'} 
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
      }
      filters={
        <>
          <SearchableSelect 
            quickFilter
            value={filters.serviceTypeId} 
            onChange={id => onChange({ ...filters, serviceTypeId: id })} 
            options={serviceTypes.map(t => ({ value: t.id, label: t.name, color: t.color }))} 
            placeholder="Tipo de serviço" 
            className={pill} 
          />
          <PersonSelector 
            quickFilter
            role="service_provider" 
            value={filters.providerId} 
            onSelect={id => onChange({ ...filters, providerId: id })} 
            placeholder="Prestador" 
            noneLabel="Todos os prestadores" 
            className={cn(pill, 'max-w-44')} 
          />
          <Select 
            value={filters.deadlineBucket ?? 'all'} 
            onValueChange={value => onChange({ ...filters, deadlineBucket: value === 'all' ? null : value as Filters['deadlineBucket'] })}
          >
            <SelectTrigger className={pill}>
              <SelectValue placeholder="Prazo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Prazo</SelectItem>
              {SERVICE_ORDER_DEADLINE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className={cn(pill, 'gap-1')} 
            onClick={() => setMore(true)}
          >
            <SlidersHorizontal className="h-3 w-3" />
            Mais
            {dateCount > 0 && <span className="text-primary">{dateCount}</span>}
          </Button>
          {count > 0 && (
            <Button 
              variant="outline" 
              className={cn(pill, 'gap-1')} 
              aria-label="Limpar filtros" 
              title="Limpar filtros" 
              onClick={() => onChange({ ...filters, serviceTypeId: null, providerId: null, deadlineBucket: null, dateFrom: null, dateTo: null, dateField: 'created_at' })}
            >
              <X className="h-3 w-3" />
              {count}
            </Button>
          )}
        </>
      }
      actions={
        <>
          {/* v1.1.0: atalho do calendário de agendamentos, igual ao da listagem de O.S. */}
          {onCalendar && (
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Abrir calendário de agendamentos"
              title="Abrir calendário de agendamentos"
              onClick={onCalendar}
            >
              <CalendarDays className="h-3.5 w-3.5" />
            </Button>
          )}
          {onList && (
            <Button 
              variant="outline" 
              size="icon-sm" 
              aria-label="Alternar para listagem" 
              title="Alternar para listagem" 
              onClick={onList}
            >
              <List className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button 
            variant="outline" 
            size="icon-sm" 
            aria-label="Atualizar Kanban" 
            title="Atualizar Kanban" 
            onClick={onRefresh}
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {onCreate && (
            <Button 
              size="sm" 
              className="h-7 px-2 sm:px-3" 
              onClick={onCreate}
            >
              <Plus className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Nova OS</span>
            </Button>
          )}
        </>
      }
    >
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="right" className="flex w-full max-w-sm flex-col p-0">
          <SheetHeader className="border-b p-4">
            <SheetTitle className="text-sm">Filtros Avançados</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 overflow-y-auto p-4">
            <Select 
              value={filters.dateField} 
              onValueChange={value => onChange({ ...filters, dateField: value as Filters['dateField'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">Criação</SelectItem>
                <SelectItem value="scheduled_start">Agendamento</SelectItem>
                <SelectItem value="completed_at">Conclusão</SelectItem>
              </SelectContent>
            </Select>
            <DateFilterRow 
              label="Período" 
              fromDate={date(filters.dateFrom)} 
              toDate={date(filters.dateTo)} 
              onFromChange={d => onChange({ ...filters, dateFrom: d ? format(d, 'yyyy-MM-dd') : null })} 
              onToChange={d => onChange({ ...filters, dateTo: d ? format(d, 'yyyy-MM-dd') : null })} 
            />
            {dateCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onChange({ ...filters, dateFrom: null, dateTo: null })}
              >
                <X className="mr-1 h-3 w-3" />
                Limpar período
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </KanbanFilterLayout>
  );
}
